import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { aiToast } from './ai-toast';

import { fetchWrapper } from '@/lib/fetch-wrapper';

export type SettingsMap = Record<string, string>;

export function useSettingsForm(initial: SettingsMap | null) {
  const [draft, setDraft] = useState<SettingsMap | null>(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const dirty = useMemo(() => {
    if (!initial || !draft) return false;
    return JSON.stringify(initial) !== JSON.stringify(draft);
  }, [initial, draft]);

  const setField = useCallback((key: string, value: string) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const reset = useCallback(() => {
    setDraft(initial);
  }, [initial]);

  return { draft, setDraft, setField, dirty, saving, setSaving, reset };
}

interface SettingsSaveBarProps {
  dirty: boolean;
  saving: boolean;
  onSave: () => void | Promise<void>;
  onReset?: () => void;
  saveTestId?: string;
  resetTestId?: string;
}

export function SettingsSaveBar({
  dirty,
  saving,
  onSave,
  onReset,
  saveTestId,
  resetTestId,
}: SettingsSaveBarProps) {
  if (!dirty && !onReset) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
      <Button
        variant="contained"
        onClick={() => onSave()}
        disabled={!dirty || saving}
        data-testid={saveTestId}>
        {saving ? 'Saving…' : 'Save settings'}
      </Button>
      {onReset && (
        <Button
          variant="outlined"
          onClick={onReset}
          disabled={!dirty || saving}
          data-testid={resetTestId}>
          Reset
        </Button>
      )}
    </Box>
  );
}

export async function saveSettings(
  url: string,
  draft: SettingsMap,
  initial: SettingsMap,
): Promise<SettingsMap> {
  const partial: SettingsMap = {};
  for (const [key, value] of Object.entries(draft)) {
    if (initial[key] !== value) {
      partial[key] = value;
    }
  }
  if (Object.keys(partial).length === 0) {
    return initial;
  }
  const updated = await fetchWrapper.put(url, partial);
  aiToast.success('Settings saved');
  return updated;
}
